import React, { useEffect, useState } from 'react';
import { useJsApiLoader } from '@react-google-maps/api';
import './HomeVisit.scss';
import DateTimePicker from './NurseAvailabilityDisplay';
import { getServices } from '@/pages/HealthTestsAndSpecialists/helpers/getData';
import { getRequest } from '@/services/api';
import HomeVisitMap from './HomeVisitMap';
import { Chip } from '@mui/material';

const cfmhProducts = [
	{
		service: {
			appt_duration: '15',
			appt_stackable: 'true',
			email_template: 'generalBloodTest',
			enable_discount: true,
			is_private: false,
			questionnaire_link: 'generalBloodTest',
			service_bookable: true,
			service_code: 'cfmhBloodTest',
			service_id: 999,
			service_name: 'CFMH blood test profile',
			service_price: 24500,
			service_status: 'true',
			service_type: 'Clinic',
		},
	},
];

const defaultCenter = {
	lat: 51.505,
	lng: -0.09,
};

/**
 * Calculates age from date of birth string.
 */
function calculateAge(dateOfBirth) {
	if (!dateOfBirth) return 0;
	const dob = new Date(dateOfBirth);
	const diff = Date.now() - dob.getTime();
	const ageDate = new Date(diff);
	return Math.abs(ageDate.getUTCFullYear() - 1970);
}

function transformAvailability(availabilityArray) {
	const result = {};
	if (!availabilityArray || !Array.isArray(availabilityArray)) return result;

	availabilityArray.forEach((block) => {
		if (!block.date || !Array.isArray(block.intervals)) return;
		result[block.date] = {
			businessOpen: true,
			availableTimeSlots: block.intervals,
		};
	});
	return result;
}

const HomeVisitForm = ({ onContinue, setBookingData }) => {
	const [currentView, setCurrentView] = useState('personalDetails');
	const [filteredNurses, setFilteredNurses] = useState([]);
	const [userLocation, setUserLocation] = useState(null);
	const [loading, setLoading] = useState(false);
	const [services, setServices] = useState([]);
	const [formData, setFormData] = useState({
		firstName: '',
		lastName: '',
		gender: '',
		dateOfBirth: '',
		phoneNumber: '',
		emailAddress: '',
		addressLine1: '',
		city: '',
		postcode: '',
		selectedService: null,
		selectedNurseId: null,
		selectedDate: '',
		selectedTime: '',
	});

	useEffect(() => {
		setLoading(true);
		getServices((data) => {
			setServices(data);
			setLoading(false);
		}).catch((error) => {
			console.error('Error fetching services:', error);
			setLoading(false);
		});
	}, []);

	// Load Google Maps script
	const { isLoaded } = useJsApiLoader({
		id: 'google-map-script',
		googleMapsApiKey: process.env.REACT_APP_GOOGLE_DISTANCE_MATRIX_API_KEY,
	});

	/**
	 * Updates form data state on input change.
	 */
	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormData((prev) => ({ ...prev, [name]: value }));
	};

	/**
	 * Geocodes the given address string using Google Maps API
	 */
	async function geocodeAddress(address) {
		if (!address) return null;
		try {
			const response = await fetch(
				`https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
					address
				)}&key=${process.env.REACT_APP_GOOGLE_GEOCODING_API_KEY}`
			);
			const data = await response.json();

			if (data.results && data.results.length > 0) {
				return data.results[0].geometry.location; // {lat, lng}
			}
		} catch (error) {
			console.error('Error geocoding address:', error);
		}
		return null;
	}

	/**
	 * Fetches nurses from the backend with query params,
	 * e.g. ?is_nurse=true&allowed_to_home_visit=true&city=...
	 */
	async function loadNursesFromAPI() {
		// Build query parameters
		const queryParams = {
			allowed_to_home_visit: 'true',
		};

		const data = await getRequest('/nurse/filter', queryParams);
		if (data && data.status === 'success') {
			return data.results; // array of nurses
		}
		console.error('Unexpected nurse API response:', data);
		return [];
	}

	/**
	 * Called when user clicks "Find availability".
	 */
	const handleFindAvailability = async () => {
		if (
			!formData.firstName ||
			!formData.lastName ||
			!formData.gender ||
			!formData.dateOfBirth ||
			!formData.phoneNumber ||
			!formData.emailAddress ||
			!formData.addressLine1 ||
			!formData.city ||
			!formData.postcode ||
			!formData.selectedService
		) {
			alert('Please fill in all required fields.');
			return;
		}

		setLoading(true);

		// 1) Calculate patient's age
		const patientAge = calculateAge(formData.dateOfBirth);

		// 2) Build user address string and geocode it
		const userFullAddress = [
			formData.addressLine1,
			formData.city,
			formData.postcode,
			'United Kingdom',
		]
			.filter((p) => p && p.trim() !== '')
			.join(', ');

		let userGeo = null;
		if (userFullAddress) {
			userGeo = await geocodeAddress(userFullAddress);
			if (userGeo) {
				setUserLocation(userGeo);
			}
		}

		// 3) Fetch nurses from the backend
		const apiNurses = await loadNursesFromAPI();

		// 4) Process each nurse (check age, geocode, prepare availability)
		const processedNurses = [];
		for (const nurse of apiNurses) {
			const youngestAllowed =
				nurse.youngest_age_phlebotomy != null ? parseFloat(nurse.youngest_age_phlebotomy) : 0;

			if (patientAge < youngestAllowed) {
				continue;
			}

			const nurseFullAddress = [nurse.address_line1, nurse.city, nurse.postcode, nurse.country]
				.filter((p) => p && p.trim() !== '')
				.join(', ');

			let nurseLatLng = null;
			if (nurseFullAddress) {
				nurseLatLng = await geocodeAddress(nurseFullAddress);
			}

			if (!nurseLatLng) {
				console.warn(`Skipping nurse ${nurse.full_name} due to invalid geocoding result.`);
				continue;
			}

			const dateTimePickerAvail = transformAvailability(nurse.availability);

			processedNurses.push({
				id: nurse.user_id || nurse.id,
				name: nurse.full_name || nurse.username || 'Unknown Nurse',
				latitude: nurseLatLng.lat,
				longitude: nurseLatLng.lng,
				// set a placeholder distance for now; will overwrite using Distance Matrix
				distanceInMiles: 0,
				youngestPatient: youngestAllowed,
				availability: dateTimePickerAvail,
				price: nurse.first_person_rate,
			});
		}

		if (userGeo && processedNurses.length > 0) {
			try {
				const distanceMatrixService = new window.google.maps.DistanceMatrixService();
				distanceMatrixService.getDistanceMatrix(
					{
						origins: [{ lat: userGeo.lat, lng: userGeo.lng }],
						destinations: processedNurses.map((n) => ({
							lat: n.latitude,
							lng: n.longitude,
						})),
						travelMode: window.google.maps.TravelMode.DRIVING,
						unitSystem: window.google.maps.UnitSystem.IMPERIAL, // for miles
					},
					(response, status) => {
						if (status === 'OK' && response?.rows?.[0]?.elements) {
							response.rows[0].elements.forEach((elem, index) => {
								if (elem.distance && elem.distance.value != null) {
									const miles = elem.distance.value * 0.000621371;
									processedNurses[index].distanceInMiles = miles.toFixed(2);
								} else {
									processedNurses[index].distanceInMiles = 'N/A';
								}
							});
							processedNurses.sort(
								(a, b) => parseFloat(a.distanceInMiles) - parseFloat(b.distanceInMiles)
							);
							const topFive = processedNurses.slice(0, 5);
							setFilteredNurses(topFive);
							setLoading(false);
							setCurrentView('mapAvailability');
						} else {
							console.error('DistanceMatrixService failed:', status);
							setFilteredNurses(processedNurses);
							setLoading(false);
							setCurrentView('mapAvailability');
						}
					}
				);
			} catch (error) {
				console.error('Error calling Distance Matrix API:', error);
			}
		}

		// 5) Sort nurses by distance
		processedNurses.sort((a, b) => parseFloat(a.distanceInMiles) - parseFloat(b.distanceInMiles));

		// 6) Slice top 5 for the UI
		const topFive = processedNurses.slice(0, 5);
		setFilteredNurses(topFive);

		setLoading(false);
		setCurrentView('mapAvailability');
	};

	const handleGoBack = () => {
		setCurrentView('personalDetails');
	};

	/**
	 * Called by <DateTimePicker /> when user selects date/time for a given nurse.
	 */
	const handleNurseDateTimeChange = ({ nurseId, date, time }) => {
		setFormData((prev) => ({
			...prev,
			selectedNurseId: nurseId,
			selectedDate: date,
			selectedTime: time,
		}));
	};

	/**
	 * Once user picks a nurse + timeslot, finalize booking data and proceed.
	 */
	const handleSubmit = () => {
		const { selectedNurseId, selectedDate, selectedTime, selectedService } = formData;
		if (!selectedNurseId || !selectedDate || !selectedTime) {
			alert('Please select a date and time before proceeding.');
			return;
		}

		const chosenNurse = filteredNurses.find((nurse) => nurse.id === selectedNurseId) || {};

		const appointmentLocation = [
			formData.addressLine1,
			formData.city,
			formData.postcode,
			'United Kingdom',
		]
			.filter((part) => part && part.trim() !== '')
			.join(', ');

		const calculateTotalPrice = (servicePrice, nursePrice) => {
			let basePrice = 0;
			try {
				basePrice = parseInt(servicePrice);

				if (!nursePrice || nursePrice === '') {
					return basePrice;
				}

				const nursePriceInCents = Math.round(parseFloat(nursePrice) * 100);

				return basePrice + nursePriceInCents;
			} catch (error) {
				// Fallback to service price if any conversion fails
				return basePrice;
			}
		};

		const newBooking = {
			booking: {
				bookingId: Date.now().toString(),
				locationId: 4,
				bookingName: `${formData.firstName} ${formData.lastName}`,
				bookingProduct: selectedService.service.service_name,
				appointmentLocation,
				appointmentRegion: 'Home Visit',
				appointmentDate: formData.selectedDate,
				appointmentTime: formData.selectedTime,
				appointmentNotes: '',
				appointmentFor: 'patient',
				sampleConsent: true,
				termsconditions: true,
				completed: true,
			},
			customer: {
				nameTitle: '',
				firstName: formData.firstName,
				lastName: formData.lastName,
				emailAddress: formData.emailAddress || 'Not provided',
				patientEmailAddress: formData.emailAddress || 'Not provided',
				gender: formData.gender,
				dateOfBirth: formData.dateOfBirth,
				phoneNumber: formData.phoneNumber,
				company: '',
				occupation: '',
				address_line_1: formData.addressLine1,
				address_line_2: '',
				city: formData.city,
				postcode: formData.postcode,
				country: 'United Kingdom',
			},
			service: {
				serviceName: selectedService.service.service_name,
				serviceId: selectedService.service.service_code,
				serviceType: selectedService.service.service_type,
				emailTemplate: selectedService.service.email_template,
				regionId: 5,
				price: calculateTotalPrice(selectedService.service.service_price, chosenNurse?.price),
				apptStackable: selectedService.service.appt_stackable,
				apptDuration: selectedService.service.appt_duration,
			},
			entity: {
				entityId: 1,
			},
			clinician: {
				clinicianId: chosenNurse.id,
				clinicianName: chosenNurse.name,
				firstPersonRate: chosenNurse.price,
			},
			source: 'Business_Partner_Booking',
		};

		setBookingData((prev) => [...prev, newBooking]);
		onContinue();
	};

	const handleServiceChange = (e) => {
		const selectedServiceId = e.target.value;
		const foundService = cfmhProducts.find(
			(svc) => svc.service.service_id.toString() === selectedServiceId
		);
		setFormData((prev) => ({
			...prev,
			selectedService: foundService,
		}));
	};

	return (
		<div className="home-visit-wrapper">
			<h1>Patient Details</h1>
			{loading && <div className="spinner">Loading...</div>}

			{currentView === 'personalDetails' && (
				<div className="form-container">
					{/* --- Personal Details Form --- */}
					<div className="row">
						<div className="col">
							<label>
								First Name <span>*</span>
							</label>
							<input
								required
								type="text"
								name="firstName"
								value={formData.firstName}
								onChange={handleChange}
							/>
						</div>
						<div className="col">
							<label>
								Last Name <span>*</span>
							</label>
							<input
								required
								type="text"
								name="lastName"
								value={formData.lastName}
								onChange={handleChange}
							/>
						</div>
					</div>

					<div className="row">
						<div className="col">
							<label>
								Gender <span>*</span>
							</label>
							<select required name="gender" value={formData.gender} onChange={handleChange}>
								<option value="" disabled>
									Select Gender
								</option>
								<option value="Male">Male</option>
								<option value="Female">Female</option>
								<option value="Other">Other</option>
							</select>
						</div>
						<div className="col">
							<label>
								Date of Birth <span>*</span>
							</label>
							<input
								required
								type="date"
								name="dateOfBirth"
								value={formData.dateOfBirth}
								onChange={handleChange}
							/>
						</div>
					</div>

					<div className="row">
						<div className="col">
							<label>
								Phone Number <span>*</span>
							</label>
							<input
								required
								type="tel"
								name="phoneNumber"
								value={formData.phoneNumber}
								onChange={handleChange}
							/>
						</div>
						<div className="col">
							<label>
								Email Address <span>*</span>
							</label>
							<input
								required
								type="email"
								name="emailAddress"
								value={formData.emailAddress}
								onChange={handleChange}
							/>
						</div>
					</div>

					<div className="row">
						<div className="col">
							<label>
								Address Line 1 <span>*</span>
							</label>
							<input
								required
								type="text"
								name="addressLine1"
								value={formData.addressLine1}
								onChange={handleChange}
							/>
						</div>
						<div className="col">
							<label>
								City <span>*</span>
							</label>
							<input
								required
								type="text"
								name="city"
								value={formData.city}
								onChange={handleChange}
							/>
						</div>
					</div>

					<div className="row">
						<div className="col">
							<label>
								Postcode <span>*</span>
							</label>
							<input
								required
								type="text"
								name="postcode"
								value={formData.postcode}
								onChange={handleChange}
							/>
						</div>
						<div className="col">
							<label>
								Blood Test <span>*</span>
							</label>
							<select
								required
								name="serviceId"
								value={formData.selectedService?.service?.service_id || ''}
								onChange={handleServiceChange}
							>
								<option value="" disabled>
									Select Blood Test
								</option>
								{cfmhProducts && cfmhProducts.length > 0 ? (
									cfmhProducts.map((service) => (
										<option key={service.service.service_id} value={service.service.service_id}>
											{service.service.service_name}
										</option>
									))
								) : (
									<option disabled>No blood tests available.</option>
								)}
							</select>
						</div>
					</div>
					<div className="submit-row mt-20">
						<button className="primary_btn ml-0" type="button" onClick={handleFindAvailability}>
							Find availability
						</button>
					</div>
				</div>
			)}

			{currentView === 'mapAvailability' && !loading && (
				<div className="map-availability-view">
					<div className="go-back-container">
						<button type="button" onClick={handleGoBack}>
							&larr; Go Back
						</button>
					</div>

					<div className="results-container">
						<div className="map-section">
							{isLoaded ? (
								// TODO: Must not get to this step if userLocation is empty or null
								<HomeVisitMap userLocation={userLocation} nurses={filteredNurses} />
							) : (
								<p>Loading Map...</p>
							)}
						</div>

						<div className="nurses-section">
							<h3>Nearest Clinician</h3>
							{filteredNurses.length === 0 ? (
								<p>No clinician found.</p>
							) : (
								filteredNurses.map((nurse) => (
									<div key={nurse.id} className="nurse-card">
										<div className="nurse-header-row">
											<div className="nurse-name-col">
												<h3>
													{nurse.name}
													{nurse.price && (
														<Chip
															className="nurse-rate"
															label={'+ £ ' + nurse.price}
															size="small"
														/>
													)}
												</h3>
											</div>
											<div className="distance-col">
												<span>{nurse.distanceInMiles} miles</span>
											</div>
										</div>
										<DateTimePicker
											key={nurse.id}
											nurseId={nurse.id}
											availabilityData={nurse.availability}
											onDateTimeChange={handleNurseDateTimeChange}
											selectedNurseId={formData.selectedNurseId}
											selectedDate={formData.selectedDate}
											selectedTime={formData.selectedTime}
										/>
									</div>
								))
							)}
						</div>
					</div>

					<div style={{ marginTop: '20px' }}>
						<button className="primary_btn" onClick={handleSubmit}>
							Proceed to booking
						</button>
					</div>
				</div>
			)}
		</div>
	);
};

export default HomeVisitForm;
